import React, { useState } from 'react';
import { 
  Card, 
  Button, 
  ProgressBar, 
  Form, 
  Alert
} from 'react-bootstrap';

const MCQQuestionSession = ({ questions, onEndSession, selectedModel, apiBaseUrl }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState(Array(questions.length).fill(null));
  const [submittedAnswers, setSubmittedAnswers] = useState(Array(questions.length).fill(false));
  const [score, setScore] = useState(0);
  const [showSummary, setShowSummary] = useState(false);

  // Ensure questions have the right structure by normalizing them
  const normalizedQuestions = questions.map(q => {
    // If it's already in the right format, return it
    if (q.options && Array.isArray(q.options) && q.correct_option) {
      return q;
    }
    
    // Fix format issues - make sure options is an array
    let options = [];
    if (q.options && !Array.isArray(q.options)) {
      // If options exist but isn't an array, try to convert it
      if (typeof q.options === 'object') {
        // Handle object-like options {A: "text", B: "text"} or {0: "text", 1: "text"}
        options = Object.values(q.options);
      } else if (typeof q.options === 'string') {
        // Handle comma-separated options "text1, text2, text3"
        options = q.options.split(',').map(o => o.trim());
      }
    } else if (q.options) {
      options = q.options;
    } else {
      // Create default options if none exist
      options = ["Option A", "Option B", "Option C", "Option D"];
    }
    
    // Ensure we have a correct option
    let correctOption = q.correct_option || options[0];
    
    return {
      ...q,
      options: options,
      correct_option: correctOption
    };
  });

  const currentQuestion = normalizedQuestions[currentQuestionIndex];

  const handleAnswerSelect = (option) => {
    if (submittedAnswers[currentQuestionIndex]) return;
    
    const newSelectedAnswers = [...selectedAnswers];
    newSelectedAnswers[currentQuestionIndex] = option;
    setSelectedAnswers(newSelectedAnswers);
  };

  const handleSubmitAnswer = () => {
    if (!selectedAnswers[currentQuestionIndex]) return;
    
    const newSubmittedAnswers = [...submittedAnswers];
    newSubmittedAnswers[currentQuestionIndex] = true;
    setSubmittedAnswers(newSubmittedAnswers);
    
    // Check if answer is correct and update score
    if (selectedAnswers[currentQuestionIndex] === currentQuestion.correct_option) {
      setScore(prevScore => prevScore + 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < normalizedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Show summary if all questions have been answered
      setShowSummary(true);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const getOptionClass = (option) => {
    if (!submittedAnswers[currentQuestionIndex]) {
      // Before submission: only highlight the selected answer in blue
      return selectedAnswers[currentQuestionIndex] === option ? 'bg-primary text-white' : 'bg-light';
    } else {
      // After submission: show correct answer in green, incorrect selection in red
      if (option === currentQuestion.correct_option) {
        return 'bg-success text-white';
      }
      if (selectedAnswers[currentQuestionIndex] === option && option !== currentQuestion.correct_option) {
        return 'bg-danger text-white';
      }
      return 'bg-light';
    }
  };

  // Calculate percentage of questions answered
  const progress = Math.round((submittedAnswers.filter(Boolean).length / normalizedQuestions.length) * 100);

  // Render summary if all questions are answered
  if (showSummary) {
    const finalScore = (score / normalizedQuestions.length) * 100;
    
    return (
      <Card>
        <Card.Body className="text-center">
          <h3 className="mb-4">Quiz Complete!</h3>
          
          <div className="mb-4">
            <h4>Your Score</h4>
            <ProgressBar 
              now={finalScore} 
              label={`${Math.round(finalScore)}%`}
              variant={finalScore >= 70 ? 'success' : finalScore >= 50 ? 'warning' : 'danger'}
              style={{ height: '30px' }}
            />
            <div className="mt-2">
              You answered {score} out of {normalizedQuestions.length} questions correctly!
            </div>
          </div>
          
          <Button variant="primary" onClick={onEndSession}>
            Start New Quiz
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>Question {currentQuestionIndex + 1} of {normalizedQuestions.length}</h4>
          <Button variant="outline-secondary" onClick={onEndSession}>
            End Quiz
          </Button>
        </div>
        
        <ProgressBar 
          now={progress} 
          label={`${progress}% Complete`}
          variant="info" 
          className="mb-4"
        />
        
        <Card className="mb-4">
          <Card.Body>
            <Card.Title>Question</Card.Title>
            <Card.Text>{currentQuestion.question}</Card.Text>
          </Card.Body>
        </Card>
        
        <div className="mb-4">
          <p className="mb-2"><strong>Choose the correct answer:</strong></p>
          
          {currentQuestion.options.map((option, index) => (
            <Card 
              key={index} 
              className={`mb-2 ${getOptionClass(option)}`}
              onClick={() => handleAnswerSelect(option)}
              style={{ cursor: submittedAnswers[currentQuestionIndex] ? 'default' : 'pointer' }}
            >
              <Card.Body className="py-2">
                <Form.Check 
                  type="radio"
                  id={`option-${index}`}
                  label={option}
                  checked={selectedAnswers[currentQuestionIndex] === option}
                  onChange={() => {}}
                  disabled={submittedAnswers[currentQuestionIndex]}
                  inline
                />
              </Card.Body>
            </Card>
          ))}
        </div>
        
        {submittedAnswers[currentQuestionIndex] && (
          <Card className="mb-4 bg-light">
            <Card.Body>
              <Card.Title>Explanation</Card.Title>
              <Card.Text>
                {currentQuestion.explanation || 
                 `The correct answer is: ${currentQuestion.correct_option}`}
              </Card.Text>
            </Card.Body>
          </Card>
        )}
        
        <div className="d-flex justify-content-between">
          <Button 
            variant="outline-primary" 
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </Button>
          
          {!submittedAnswers[currentQuestionIndex] ? (
            <Button 
              variant="primary" 
              onClick={handleSubmitAnswer}
              disabled={selectedAnswers[currentQuestionIndex] === null}
            >
              Submit Answer
            </Button>
          ) : (
            <Button 
              variant="primary" 
              onClick={handleNextQuestion}
            >
              {currentQuestionIndex < normalizedQuestions.length - 1 ? 'Next' : 'View Results'}
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default MCQQuestionSession;